import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, UserPlus } from "lucide-react";
import AppLayout from "../../shared/components/AppLayout";
import Button from "../../shared/components/Button";
import patientService from "../../services/patientService";
import { DoctorPatient, GENDER_OPTIONS } from "../../types/patient";

const GENDER_LABEL: Record<string, string> = Object.fromEntries(
  GENDER_OPTIONS.map((o) => [o.value, o.label]),
);

const PatientsListPage: React.FC = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");

  const [patients, setPatients] = useState<DoctorPatient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 20;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const loadPatients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await patientService.fetchDoctorPatients({
        search: debouncedSearch || undefined,
        page: currentPage,
        page_size: PAGE_SIZE,
      });
      setPatients(response.results);
      setTotalCount(response.count);
    } catch (err: any) {
      setError(
        err.response?.data?.detail || err.message || "Failed to load patients",
      );
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, currentPage]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const getInitials = (p: DoctorPatient) =>
    `${p.first_name.charAt(0)}${p.last_name.charAt(0)}`.toUpperCase() ||
    p.name.charAt(0).toUpperCase();

  return (
    <AppLayout active="patients">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Patients</h1>
            <p className="text-gray-500 mt-1 text-sm">
              {totalCount} patient{totalCount !== 1 ? "s" : ""} in your care
            </p>
          </div>
          <Button icon={UserPlus} onClick={() => navigate("/patients/new")}>
            New Patient
          </Button>
        </div>

        {/* Search bar */}
        <div className="mb-6">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              &#128269;
            </span>
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : error ? (
          <div className="p-6 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm text-center">
            {error}
            <button
              onClick={loadPatients}
              className="ml-3 underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        ) : patients.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-5xl mb-4">👤</div>
            <h3 className="text-lg font-semibold text-gray-700">
              {debouncedSearch
                ? "No patients match your search"
                : "No patients yet"}
            </h3>
            <p className="text-gray-500 mt-2 text-sm">
              {debouncedSearch
                ? "Try a different search term."
                : "Create your first patient to get started."}
            </p>
            {!debouncedSearch && (
              <button
                onClick={() => navigate("/patients/new")}
                className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
              >
                Create Patient
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">
                      Patient
                    </th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">
                      Gender
                    </th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">
                      DOB
                    </th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">
                      Phone
                    </th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-600">
                      Added
                    </th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {patients.map((patient) => (
                    <tr
                      key={patient.patient_id}
                      className="hover:bg-blue-50 cursor-pointer transition"
                      onClick={() =>
                        navigate(`/patients/${patient.patient_id}`)
                      }
                    >
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center font-semibold text-blue-700 text-sm">
                            {getInitials(patient)}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">
                              {patient.first_name} {patient.last_name}
                            </div>
                            {patient.email &&
                              !patient.email.includes("@placeholder.docfi") && (
                                <div className="text-gray-500 text-xs">
                                  {patient.email}
                                </div>
                              )}
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-gray-600">
                        {GENDER_LABEL[patient.gender] || patient.gender || "—"}
                      </td>
                      <td className="px-5 py-4 text-gray-600">
                        {patient.dob
                          ? new Date(
                              patient.dob + "T00:00:00",
                            ).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="px-5 py-4 text-gray-600">
                        {patient.phone || "—"}
                      </td>
                      <td className="px-5 py-4 text-gray-500 text-xs">
                        {new Date(patient.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <Button
                            variant="success"
                            size="sm"
                            icon={Mic}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(
                                `/patients/${patient.patient_id}/record`,
                              );
                            }}
                          >
                            Record
                          </Button>
                          <span className="text-blue-600 hover:underline text-xs font-medium">
                            View →
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <p className="text-sm text-gray-500">
                  Showing {(currentPage - 1) * PAGE_SIZE + 1}–
                  {Math.min(currentPage * PAGE_SIZE, totalCount)} of{" "}
                  {totalCount} patients
                </p>
                <div className="flex gap-2">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition"
                  >
                    ← Prev
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const page =
                      Math.max(1, Math.min(currentPage - 2, totalPages - 4)) +
                      i;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1.5 text-sm border rounded-lg transition ${
                          page === currentPage
                            ? "bg-blue-600 text-white border-blue-600"
                            : "border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition"
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
};

export default PatientsListPage;
